import React, { useState, useEffect } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Card,
  CardContent,
  Snackbar,
} from '@mui/material';
import { api } from '../../services/api.js';
import { mapServerQuestionsToLocal, pollResearchStatus, mapServerResponsesToLocal } from '../../services/apiUtils.js';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import { useAuth } from '../../authentication/AuthContext.js';
import { useNavigate } from 'react-router-dom';
import { surveyLimitService } from '../../services/surveyLimitService.js';
import { event } from '../../services/analyticsService.js';
import SurveyEditor from './SurveyEditor.js';

// Research types definition
const researchTypes = [
  {
    id: 'general',
    name: 'General Research',
    description: 'Conduct a general research study tailored to your specific needs. This option is flexible and can be customized for any research objective.',
    example: 'Example: "I want to understand consumer preferences for online learning platforms, including preferred features, pricing models, and content delivery methods."'
  },
  {
    id: 'product-validation',
    name: 'Product Validation',
    description: 'Quickly assess the viability and appeal of your startup concept, new product, or identified market problem. This ensures you focus on solutions that genuinely meet customer needs, minimizing risk and investment uncertainty.',
    example: 'Example: "I want to validate my idea for a meal planning app that automatically generates grocery lists based on dietary preferences and budget constraints. I want to understand whether users find this concept appealing and what features they would prioritize. I also want questions on what type of customers this appeals to most"'
  },
  {
    id: 'branding-feedback',
    name: 'Branding Feedback',
    description: 'Understand how your brand identity is perceived by your intended audience. You want to find out how to optimize brand perception, build stronger emotional connections, and enhance overall brand loyalty.',
    example: 'Example: "I want feedback on my eco-friendly clothing brand\'s new slogan and the messaging we have around sustainability, to see how it resonates with environmentally conscious consumers. We are targeting a Gen Z audience, so give me questions that will help me understand how to optimize our brand\'s perception with them."'
  },
  {
    id: 'campaign-validation',
    name: 'Campaign Idea Validation',
    description: 'Test campaign ideas, messages, or concepts before launching them widely. Your goal is to ensure your marketing efforts resonate effectively with your audience, maximizing campaign impact and ROI.',
    example: 'Example: "I want to test my social media campaign concept for promoting our new fitness tracker, focusing on the \'30-day transformation challenge\' messaging. We want ideas on where to focus our marketing efforts, given our target audience of health-conscious elders."'
  }
];

function Step1Survey({ userData, setUserData, onTopicsChange, setTabIndex, onDuplicateResearch }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pollingSurvey, setPollingStatus] = useState(false);
  const [openWarningSnackbar, setOpenWarningSnackbar] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [researchType, setResearchType] = useState('general'); // Default to general research
  
  const { walletBalance, isAdmin, hasActiveSubscription, credits, updateUserAttributes } = useAuth();
  const navigate = useNavigate();

// Effect to poll for survey status
useEffect(() => {
  console.log("🔥 POLLING useEffect triggered - Survey Status:", userData.surveyStatus);
  console.log("UserData is:", userData);

  if (userData.surveyStatus === "PENDING" && !pollingSurvey && userData.researchID) {
    const pollForSurvey = async () => {
      setPollingStatus(true);
      try {
        const research = await pollResearchStatus(userData.userID, userData.researchID, 'surveyStatus');
        console.log("Survey completed!");
        const survey = research.survey;
        if (survey) {
          const formattedQuestions = mapServerQuestionsToLocal(survey.questions, survey.order);
          console.log("FORMATTED QUESTIONS IS", formattedQuestions);
          
          // Update userData with the survey data
          const updatedUserData = {
            ...userData,
            surveyQuestions: formattedQuestions,
            surveyDescription: survey.description,
            surveyStatus: 'COMPLETED',
            lastModified: research.lastModified,
            title: research.title || userData.title
          };
          
          setUserData(updatedUserData);
          
          console.log("WE ARE DONE POLLING AND UPDATED USER DATA TO", updatedUserData);

          // Trigger topics refresh after survey is completed to ensure data is saved
          if (onTopicsChange) {
            onTopicsChange(research.title);
            
            // Force a full reload of all topics to ensure sidebar is updated
            try {
              // We'll call the same API that initializes the topics list in ResearchPage.js
              const loadedTopics = await api.getPreviousResearches(userData.userID);
              const responsesList = await api.getPreviousResponseData(userData.userID);
              const personasList = await api.getAllPreviousPersonas(userData.userID);
              
              // This is basically the same merging logic from the initial load in ResearchPage.js
              const mergedTopics = loadedTopics.map((topic) => {
                const match = responsesList.find(item => item.researchID === topic.researchID);
                const surveyResponses = mapServerResponsesToLocal(
                  match?.responses || null,
                  match?.researchID || null
                );

                const personasData = personasList.find(item => item.researchID === topic.researchID);
                const selectedPersonas = personasData?.personas || [];
                const personaFilters = personasData?.filters || [];          

                return { 
                  ...topic, 
                  surveyResponses, 
                  selectedPersonas, 
                  personaFilters
                };
              });
              
              // Update the global topics state in ResearchPage.js
              // We need a way to communicate this update to the parent component
              // For now, let's modify onTopicsChange to accept this data
              onTopicsChange(research.title, mergedTopics);
            } catch (error) {
              // console.error('Error refreshing topics list:', error);
            }
          }
        }
      } catch (err) {
        console.log("Error when waiting for survey generation");
        setError(`Error polling for survey: ${err.message}`);
      } finally {
        setPollingStatus(false);
      }
    };
    pollForSurvey();
  }
}, [userData.surveyStatus, userData.userID, userData.researchID, loading, pollingSurvey, setUserData, onTopicsChange, userData]);

const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError(null);

  // Track survey submission
  event({
    action: 'research_step_started',
    category: 'tool_usage', 
    label: 'step_1_survey_creation',
  });

  try {
    // Check survey limits
    // In Step1Survey.js handleSubmit function
    const eligibility = await surveyLimitService.canCreateSurvey(userData, { isAdmin, credits });    
    if (!eligibility.canCreate) {
        // Show enhanced error message with subscription link
        setError(eligibility.message);
        // Show warning snackbar with action button
        setWarningMessage("You need a subscription or more credits to continue.");
        setOpenWarningSnackbar(true);
        setLoading(false);
        return;
      }

    // If we reach here, user is eligible (either free, paid, or admin)
    const result = await api.createSurvey(userData.userID, userData.description, researchType);
    const researchID = result.researchID; 
    const title = result.title;

    // Update last used date
    await surveyLimitService.updateLastUsedDate();
    
    const updatedUserData = {
      ...userData,
      userID: userData.userID,
      researchID: researchID,
      title: title,
      researchType: researchType,
      surveyStatus: 'PENDING',
      lastModified: Date.now() / 1000,
      responsesStatus: 'NOT_STARTED'
    };
    
    setUserData(updatedUserData);

    console.log("Created survey, responsesStatus is", updatedUserData.responsesStatus);

    // Create a new topic object to add to the topics list
    const newTopic = {
      userID: userData.userID,
      researchID: researchID,
      title: title,
      description: userData.description,
      researchType: researchType,
      surveyStatus: 'PENDING',
      lastModified: Math.floor(Date.now() / 1000),
      responsesStatus: 'NOT_STARTED',
      personasStatus: null,
      survey: null,
      surveyResponses: null,
      selectedPersonas: [],
      personaFilters: []
    };

    // Immediately update the topics list with the new topic
    // We'll use onTopicsChange to update the parent component's state
    if (onTopicsChange) {
      onTopicsChange(title, null, newTopic);
    }

  } catch (err) {
    // Track error
    event({
      action: 'research_step_error',
      category: 'error',
      label: `survey_creation_error: ${err.message}`,
    });
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  const emotionCache = createCache({
    key: 'mui-style',
    prepend: true,
  });

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={ResearchTheme}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Create Survey
          </Typography>
          
          {/* Research Type Dropdown Section - only show when no survey questions yet */}
          {!userData.researchID && (
            <Paper sx={{ p: 3, mt: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Select Research Type
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Each of these options will modify the survey generation process, to tailor it towards your specific needs. Click on each of the options to see a detailed explanation. 
                <br /> <br />
                For customer questions, ask questions that can be used to narrow down the target audience for your research. For example, "What is your opinion on apples?". You can narrow down their preferences later on, by forcing specific answers or by giving them specific knowledge.
                <br /> <br />
                In case you are not sure which one to choose, you can always select "General Research" and modify the questions later. <br />
              </Typography>
              
              <Grid container spacing={2}>
                {researchTypes.map(type => (
                  <Grid item xs={12} sm={6} md={3} key={type.id}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        cursor: 'pointer',
                        border: researchType === type.id ? '2px solid #4a90e2' : '1px solid #e0e0e0',
                        boxShadow: researchType === type.id ? '0 0 10px rgba(74, 144, 226, 0.3)' : 'none',
                      }}
                      onClick={() => setResearchType(type.id)}
                    >
                      <CardContent>
                        <Typography variant="h6" component="div" gutterBottom>
                          {type.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Survey description form - only show when no survey questions yet */}
          {!userData.surveyQuestions?.length && (
            <Paper sx={{ p: 3, mt: 2 }}>
              <Box component="form" onSubmit={handleSubmit}>
                {/* Research Type Description Box */}
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 1,
                  border: '1px solid #e0e0e0'
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {researchTypes.find(type => type.id === researchType)?.description || 'Conduct a general research study tailored to your specific needs.'}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>
                    {researchTypes.find(type => type.id === researchType)?.example || 'Example: "I want to understand consumer preferences for online learning platforms, including preferred features, pricing models, and content delivery methods."'}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="I want a survey about ..."
                  multiline
                  rows={4}
                  value={userData.description}
                  onChange={(e) => {
                    setUserData(prev => ({ ...prev, description: e.target.value }));
                    setError(null); // Clear the error when the form data changes
                  }}
                  margin="normal"
                  required
                />

                {error && (
                  <Typography color="error" sx={{ mt: 2 }}>
                    Error: {error}
                  </Typography>
                )}

                <Button
                  variant="contained"
                  type="submit"
                  disabled={loading || pollingSurvey}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Survey'}
                </Button>
              </Box>
              
            </Paper>
          )}

          {/* Show polling state when we have a researchID but no survey questions yet */}
          {userData.researchID && !userData.surveyQuestions?.length && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Generating your survey...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This may take a minute or two. Please wait.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Use the SurveyEditor component when we have survey questions */}
          {userData.surveyQuestions && userData.surveyQuestions.length > 0 && (
            <SurveyEditor 
              userData={userData} 
              setUserData={setUserData} 
              onTopicsChange={onTopicsChange}
              setTabIndex={setTabIndex}
              onDuplicateResearch={onDuplicateResearch}
              isAdmin={isAdmin}
              hasActiveSubscription={hasActiveSubscription}
              walletBalance={walletBalance}
              credits={credits}
              updateUserAttributes={updateUserAttributes}
            />
          )}

          <Snackbar
          open={openWarningSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenWarningSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setOpenWarningSnackbar(false)} 
            severity="warning"
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={() => navigate('/account', { state: { subscribe: true } })}
              >
                Go to Account
              </Button>
            }
          >
            {warningMessage}
            </Alert>
          </Snackbar>
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default Step1Survey;